import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../App';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Building2, TrendingUp, Wallet, Banknote, AlertTriangle, RefreshCcw } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import { toast } from 'sonner';

const fmt = (n) => (n == null ? '—' : Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 }));

const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#a78bfa', '#ef4444', '#06b6d4'];

const BUCKET_OPTIONS = [
  { key: 'maintenance', label: 'Maintenance' },
  { key: 'utility', label: 'Utilities' },
  { key: 'payroll', label: 'Payroll' },
  { key: 'supplier_bills', label: 'Supplier bills' },
];

export default function ExecutiveDashboard() {
  const today = new Date().toISOString().slice(0, 10);
  const yearStart = new Date(new Date().getFullYear(), 0, 1).toISOString().slice(0, 10);
  const [from, setFrom] = useState(yearStart);
  const [to, setTo] = useState(today);
  const [buckets, setBuckets] = useState(['maintenance', 'utility', 'payroll']);
  const [kpi, setKpi] = useState(null);
  const [cpp, setCpp] = useState(null);
  const [trend, setTrend] = useState(null);
  const [eff, setEff] = useState(null);
  const [busy, setBusy] = useState(false);

  const fetchAll = async () => {
    setBusy(true);
    try {
      const q = `?date_from=${from}&date_to=${to}`;
      const [k, c, t, e] = await Promise.all([
        axios.get(`${API}/executive/kpis${q}`, { withCredentials: true }),
        axios.get(`${API}/executive/cost-per-property${q}&buckets=${buckets.join(',')}`, { withCredentials: true }),
        axios.get(`${API}/executive/cost-trend?months=12`, { withCredentials: true }),
        axios.get(`${API}/executive/asset-efficiency`, { withCredentials: true }),
      ]);
      setKpi(k.data); setCpp(c.data); setTrend(t.data); setEff(e.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  useEffect(() => { fetchAll(); /* eslint-disable-line */ }, [from, to]);
  useEffect(() => { if (kpi) fetchAll(); /* eslint-disable-line */ }, [buckets.join(',')]);

  const toggleBucket = (k) => setBuckets((b) => b.includes(k) ? b.filter((x) => x !== k) : [...b, k]);

  const costMix = kpi ? [
    { name: 'Maintenance', value: kpi.maintenance_cost },
    { name: 'Utilities', value: kpi.utility_cost },
    { name: 'Payroll', value: kpi.payroll_cost },
  ].filter((x) => x.value > 0) : [];

  return (
    <Layout>
      <div data-testid="executive-dashboard" className="space-y-6">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2 text-slate-900 dark:text-slate-100"><TrendingUp className="w-7 h-7 text-amber-500" />Executive Dashboard</h1>
            <p className="text-sm text-muted-foreground dark:text-slate-400">Portfolio-wide cost & efficiency view for the CEO.</p>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <div><Label className="text-xs">From</Label><Input data-testid="exec-from" type="date" className="h-9 w-[140px]" value={from} onChange={(e) => setFrom(e.target.value)} /></div>
            <div><Label className="text-xs">To</Label><Input data-testid="exec-to" type="date" className="h-9 w-[140px]" value={to} onChange={(e) => setTo(e.target.value)} /></div>
            <Button data-testid="exec-refresh" size="sm" variant="outline" onClick={fetchAll} disabled={busy}><RefreshCcw className="w-3 h-3 mr-1" />Refresh</Button>
          </div>
        </div>

        {/* KPI tiles */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Card className="p-4"><div className="text-xs text-muted-foreground uppercase tracking-wide">Buildings</div><div className="text-2xl font-bold mt-1">{kpi?.buildings ?? '—'}</div></Card>
          <Card data-testid="kpi-maintenance" className="p-4 bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200"><div className="text-xs text-amber-700 uppercase tracking-wide">Maintenance Cost</div><div className="text-2xl font-bold mt-1 text-amber-900">{fmt(kpi?.maintenance_cost)}</div></Card>
          <Card data-testid="kpi-opex" className="p-4 bg-gradient-to-br from-rose-50 to-rose-100 border-rose-200"><div className="text-xs text-rose-700 uppercase tracking-wide">Operating Cost</div><div className="text-2xl font-bold mt-1 text-rose-900">{fmt(kpi?.operating_cost)}</div></Card>
          <Card data-testid="kpi-recovered" className="p-4 bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200"><div className="text-xs text-emerald-700 uppercase tracking-wide">Recoverable Revenue</div><div className="text-2xl font-bold mt-1 text-emerald-900">{fmt(kpi?.recoverable_revenue)}</div></Card>
        </div>

        {/* Cost trend + mix */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card className="p-4 lg:col-span-2">
            <h2 className="font-semibold mb-3">Operating cost trend (12 mo)</h2>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={trend?.months || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.2)" />
                <XAxis dataKey="month" fontSize={11} />
                <YAxis fontSize={11} />
                <Tooltip />
                <Legend />
                <Bar dataKey="maintenance" stackId="a" fill="#f59e0b" />
                <Bar dataKey="utility" stackId="a" fill="#3b82f6" />
                <Bar dataKey="payroll" stackId="a" fill="#a78bfa" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
          <Card className="p-4">
            <h2 className="font-semibold mb-3">Cost mix</h2>
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={costMix} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={(d) => `${d.name}: ${fmt(d.value)}`}>
                  {costMix.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </div>

        {/* Cost per property */}
        <Card className="p-0 overflow-hidden">
          <div className="p-4 flex flex-wrap items-center justify-between gap-3 border-b">
            <h2 className="font-semibold flex items-center gap-2"><Building2 className="w-4 h-4 text-amber-600" />Cost per property</h2>
            <div className="flex flex-wrap items-center gap-3 text-xs">
              {BUCKET_OPTIONS.map((o) => (
                <label key={o.key} className="inline-flex items-center gap-1 cursor-pointer">
                  <Checkbox checked={buckets.includes(o.key)} onCheckedChange={() => toggleBucket(o.key)} data-testid={`exec-bucket-${o.key}`} />
                  <span>{o.label}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[760px]">
              <thead className="bg-slate-50 dark:bg-slate-900/50 text-xs uppercase text-slate-600">
                <tr>
                  <th className="px-3 py-2 text-left">Building</th>
                  <th className="text-right">Maint</th>
                  <th className="text-right">Utility</th>
                  <th className="text-right">Payroll</th>
                  <th className="text-right">Supplier</th>
                  <th className="text-right">Total Opex</th>
                  <th className="text-right">$/sq.ft</th>
                  <th className="text-right">Recovered</th>
                  <th className="text-right">Net</th>
                </tr>
              </thead>
              <tbody>
                {(cpp?.rows || []).slice(0, 25).map((r, i) => (
                  <tr key={r.building_id} data-testid={`cpp-row-${i}`} className="border-t hover:bg-slate-50/60 dark:hover:bg-slate-800/40">
                    <td className="px-3 py-2 font-medium">{r.building_name}</td>
                    <td className="text-right">{fmt(r.maintenance)}</td>
                    <td className="text-right">{fmt(r.utility)}</td>
                    <td className="text-right">{fmt(r.payroll)}</td>
                    <td className="text-right">{fmt(r.supplier_bills)}</td>
                    <td className="text-right font-semibold">{fmt(r.total_opex)}</td>
                    <td className="text-right">{fmt(r.cost_per_sqft)}</td>
                    <td className="text-right text-emerald-700 dark:text-emerald-400">{fmt(r.tenant_invoiced)}</td>
                    <td className={`text-right font-semibold ${r.net_margin >= 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-rose-700 dark:text-rose-400'}`}>{fmt(r.net_margin)}</td>
                  </tr>
                ))}
                {(!cpp?.rows || cpp.rows.length === 0) && (
                  <tr><td colSpan={9} className="text-center py-8 text-slate-500">No data in the selected period.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Asset efficiency */}
        <Card className="p-0 overflow-hidden">
          <div className="px-4 py-3 border-b flex items-center justify-between">
            <h2 className="font-semibold flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-rose-500" />Asset Efficiency — top {eff?.rows?.length ?? 0} by downtime (YoY)</h2>
            <span className="text-xs text-muted-foreground">{eff?.year} vs {eff?.previous_year}</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
              <thead className="bg-slate-50 dark:bg-slate-900/50 text-xs uppercase text-slate-600">
                <tr>
                  <th className="px-3 py-2 text-left">Asset</th>
                  <th>Category</th>
                  <th>Status</th>
                  <th className="text-right">WOs</th>
                  <th className="text-right">Downtime (h)</th>
                  <th className="text-right">Last year (h)</th>
                  <th className="text-right">YoY %</th>
                </tr>
              </thead>
              <tbody>
                {(eff?.rows || []).map((r, i) => (
                  <tr key={r.asset_id} data-testid={`asset-eff-row-${i}`} className="border-t">
                    <td className="px-3 py-2 font-medium">{r.asset_name}</td>
                    <td className="text-center capitalize">{r.category || '—'}</td>
                    <td className="text-center capitalize">{r.status || '—'}</td>
                    <td className="text-right">{r.wo_count}</td>
                    <td className="text-right">{fmt(r.downtime_hours)}</td>
                    <td className="text-right text-slate-500">{fmt(r.downtime_hours_previous)}</td>
                    <td className={`text-right font-semibold ${r.yoy_change_pct == null ? '' : r.yoy_change_pct > 0 ? 'text-rose-600' : 'text-emerald-600'}`}>{r.yoy_change_pct == null ? '—' : `${r.yoy_change_pct}%`}</td>
                  </tr>
                ))}
                {(!eff?.rows || eff.rows.length === 0) && (
                  <tr><td colSpan={7} className="text-center py-8 text-slate-500">No asset downtime data yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </Layout>
  );
}
